import { Icon } from '@iconify/react'
import { PhoneWhatsAppLink } from '@/components/PhoneWhatsAppLink'
import type { RaspadinhaParticipant, RaspadinhaTimeSlotWinner } from '@/types/raspadinha-campaign'
import {
  formatParticipantDateTime,
  formatReceiptSignedAt,
  formatWinningTimeLabel,
  getWinnerPrizeReceiptSignature,
} from '@/lib/raspadinha-campaigns'
import styles from './RaspadinhaWeeklyWinningSchedules.module.css'

type WinnerDetailPanelProps = {
  winner: RaspadinhaTimeSlotWinner
  slotTime: string
  dateLabel?: string
  slotIsActive?: boolean
  participant?: RaspadinhaParticipant | null
}

type DetailFieldProps = {
  label: string
  value: string
  mono?: boolean
}

function DetailField({ label, value, mono = false }: DetailFieldProps) {
  const trimmed = value.trim()
  return (
    <div className={styles.winnerDetailField}>
      <span className={styles.winnerDetailLabel}>{label}</span>
      <span className={mono ? styles.winnerDetailValueMono : styles.winnerDetailValue}>
        {trimmed || '—'}
      </span>
    </div>
  )
}

export function WinnerDetailPanel({
  winner,
  slotTime,
  dateLabel,
  slotIsActive,
  participant,
}: WinnerDetailPanelProps) {
  const receipt = getWinnerPrizeReceiptSignature(winner)

  return (
    <div className={styles.winnerDetailPanel}>
      <p className={styles.winnerDetailTitle}>
        <Icon icon="mdi:account-details-outline" aria-hidden />
        Detalhes do ganhador
      </p>

      <div className={styles.winnerDetailGrid}>
        <DetailField label="Nome completo" value={winner.name} />
        <DetailField label="CPF / documento" value={winner.document} />
        <DetailField label="Prêmio" value={winner.prize_label} />
        {dateLabel ? <DetailField label="Data da contemplação" value={dateLabel} /> : null}
        <DetailField label="Horário da contemplação" value={formatWinningTimeLabel(slotTime)} />
        {slotIsActive !== undefined ? (
          <DetailField label="Status do horário" value={slotIsActive ? 'Ativo' : 'Inativo'} />
        ) : null}
        <DetailField label="ID interno" value={winner.id} mono />
        <DetailField label="Observações" value={winner.notes} />
      </div>

      {participant ? (
        <section
          className={styles.receiptSignatureSection}
          aria-labelledby={`participant-${winner.id}`}
        >
          <h5 id={`participant-${winner.id}`} className={styles.receiptSignatureTitle}>
            <Icon icon="mdi:account-circle-outline" aria-hidden />
            Dados de participação
          </h5>
          <div className={styles.receiptSignatureGrid}>
            <DetailField label="E-mail" value={participant.email} />
            <div className={styles.winnerDetailField}>
              <span className={styles.winnerDetailLabel}>WhatsApp</span>
              <PhoneWhatsAppLink
                phone={participant.phone}
                contactName={participant.name}
              />
            </div>
            <DetailField
              label="Data de participação"
              value={formatParticipantDateTime(participant.participated_at)}
            />
            <DetailField label="Cartelas" value={String(participant.tickets_count)} />
            <DetailField label="ID do participante" value={participant.id} mono />
          </div>
        </section>
      ) : null}

      <section className={styles.receiptSignatureSection} aria-labelledby={`receipt-${winner.id}`}>
        <h5 id={`receipt-${winner.id}`} className={styles.receiptSignatureTitle}>
          <Icon icon="mdi:file-sign" aria-hidden />
          Assinatura de recebimento do prêmio
        </h5>
        <p className={styles.receiptSignatureHint}>
          Registro simulado para auditoria do protótipo. Em produção, estes dados viriam do backend.
        </p>
        <div className={styles.receiptSignatureGrid}>
          <DetailField label="Hash" value={receipt.hash} mono />
          <DetailField label="Endereço IP" value={receipt.ip_address} mono />
          <DetailField
            label="Data e hora"
            value={formatReceiptSignedAt(receipt.signed_at)}
            mono
          />
        </div>
      </section>
    </div>
  )
}
